import { default as React, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { BaseCardProps } from '../../shared/shared';
import { SignupFormDataProvider } from './SignupFormDataContext';
import { ensureUnreachable } from '@signup/shared/src/utils/ensure';
import { SignupEventType } from '@signup/shared';
import { SignupFormStepOneModified } from './SignupFormStepOneModified';
import { SignupFormStepTwoModified } from './SignupFormStepTwoModified';
import { SignupCompleteCard } from './SignupCompleteCard';
import {
  SignupFormControllerProvider,
  useSignupFormController,
} from './SignupFormControllerContext';
import { useLogOnClose } from '../../../UseLogOnClose';
import { SignupEventName } from '../../../../utils/SignupLogger';
import { useTelemetry } from '@snowflake/core-ui';
import { useSignupFlowType } from '../../../../utils/useSignupFlowType';

export enum SignupReason {
  CompanyConsidering = 'Company is considering Snowflake',
  JobTraining = 'Training or Certification',
  PersonalTraining = 'Personal learning and development',
  Student = 'Student',
  Other = 'Other',
}

enum SignupCardStage {
  SignupForm = 'SignupForm',
  EmailVerification = 'EmailVerification',
}

enum SignupCardStep {
  SignupFormOne = 'SignupFormOne',
  SignupFormTwo = 'SignupFormTwo',
  EmailSent = 'EmailSent',
  Completed = 'Completed',
}

type SignupCardState =
  | {
      stage: SignupCardStage.SignupForm;
      step: SignupCardStep.SignupFormOne | SignupCardStep.SignupFormTwo;
    }
  | {
      stage: SignupCardStage.EmailVerification;
      step: SignupCardStep.EmailSent | SignupCardStep.Completed;
    };

function useNavigationControl() {
  const [state, setState] = useState<SignupCardState>({
    stage: SignupCardStage.SignupForm,
    step: SignupCardStep.SignupFormOne,
  });

  const [currentStateStartTime, setCurrentStateStartTime] = useState<number>(Date.now() / 1000);

  useEffect(() => {
    setCurrentStateStartTime(Date.now() / 1000);
  }, [state.stage, state.step]);

  const signupFlowType = useSignupFlowType();
  const prevSignupFlowType = useRef(signupFlowType);
  useEffect(() => {
    if (prevSignupFlowType.current === signupFlowType) return;
    prevSignupFlowType.current = signupFlowType;
    setState({ stage: SignupCardStage.SignupForm, step: SignupCardStep.SignupFormOne });
  }, [signupFlowType]);

  const continueNext = useCallback(() => {
    switch (state.stage) {
      case SignupCardStage.SignupForm:
        switch (state.step) {
          case SignupCardStep.SignupFormOne:
            // Go directly to email verification, skip Form 2
            return setState({
              stage: SignupCardStage.EmailVerification,
              step: SignupCardStep.EmailSent,
            });
          case SignupCardStep.SignupFormTwo:
            return setState({
              stage: SignupCardStage.EmailVerification,
              step: SignupCardStep.EmailSent,
            });
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.EmailSent:
            return setState({
              stage: SignupCardStage.EmailVerification,
              step: SignupCardStep.Completed,
            });
          case SignupCardStep.Completed:
            return null;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state, setState]);

  const goBack = useCallback(() => {
    switch (state.stage) {
      case SignupCardStage.SignupForm:
        switch (state.step) {
          case SignupCardStep.SignupFormOne:
            return null;
          case SignupCardStep.SignupFormTwo:
            return setState({
              stage: SignupCardStage.SignupForm,
              step: SignupCardStep.SignupFormOne,
            });
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.EmailSent:
            return null;
          case SignupCardStep.Completed:
            return null;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state, setState]);

  const restartWithoutClearInputs = useCallback(() => {
    setState({
      stage: SignupCardStage.SignupForm,
      step: SignupCardStep.SignupFormOne,
    });
  }, [setState]);

  const exitEvent = useMemo((): SignupEventName => {
    switch (state.stage) {
      case SignupCardStage.SignupForm:
        switch (state.step) {
          case SignupCardStep.SignupFormOne:
            return SignupEventName.EXIT_SIGNUP_MODIFIED_FORM_ONE;
          case SignupCardStep.SignupFormTwo:
            return SignupEventName.EXIT_SIGNUP_MODIFIED_FORM_TWO;
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.EmailSent:
            return SignupEventName.EXIT_SIGNUP_THANK_YOU_PAGE;
          case SignupCardStep.Completed:
            return SignupEventName.EXIT_SIGNUP_THANK_YOU_PAGE;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state]);

  return {
    state,
    setState,
    continueNext,
    goBack,
    restartWithoutClearInputs,
    exitEvent,
    currentStateStartTime,
  };
}

const SignupCardV2ModifiedImpl = () => {
  const {
    state,
    setState,
    continueNext,
    goBack,
    restartWithoutClearInputs,
    exitEvent,
    currentStateStartTime,
  } = useNavigationControl();
  const { clearCreationErrorAndResetRecaptcha } = useSignupFormController();
  const { logEvent } = useTelemetry();
  const [fadeOut, setFadeOut] = useState(false);

  useLogOnClose({ message: exitEvent, startTime: currentStateStartTime });

  // Apply a 250ms fade to any action
  const withTransition = useCallback((action: () => void) => {
    setFadeOut(true);
    setTimeout(() => {
      action();
      setFadeOut(false);
    }, 250);
  }, []);

  const handleContinueNext = useCallback(() => {
    withTransition(() => continueNext());
  }, [continueNext, withTransition]);

  const handleGoBack = useCallback(() => {
    logEvent({
      event: SignupEventName.SIGNUP_MODIFIED_BACK_TO_FORM_ONE,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        flowType: 'modified',
        timeOnFormTwo: Date.now() / 1000 - currentStateStartTime,
      },
      interaction: true,
    });
    withTransition(() => goBack());
  }, [goBack, logEvent, currentStateStartTime, withTransition]);

  const handleGoToPlanOptions = useCallback(() => {
    logEvent({
      event: SignupEventName.SIGNUP_MODIFIED_PLAN_OPTIONS_CLICKED,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        flowType: 'modified',
        action: 'open_form_two',
      },
      interaction: true,
    });

    logEvent({
      event: SignupEventName.SIGNUP_MODIFIED_FORM_TWO_OPENED,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        flowType: 'modified',
        timeOnFormOne: Date.now() / 1000 - currentStateStartTime,
      },
      interaction: false,
    });

    withTransition(() => {
      setState({
        stage: SignupCardStage.SignupForm,
        step: SignupCardStep.SignupFormTwo,
      });
    });
  }, [setState, logEvent, currentStateStartTime, withTransition]);

  const handleTryAgain = useCallback(() => {
    withTransition(() => {
      restartWithoutClearInputs();
      clearCreationErrorAndResetRecaptcha();
    });
  }, [restartWithoutClearInputs, clearCreationErrorAndResetRecaptcha, withTransition]);

  const render = useCallback(() => {
    switch (state.stage) {
      case SignupCardStage.SignupForm:
        switch (state.step) {
          case SignupCardStep.SignupFormOne:
            return (
              <SignupFormStepOneModified
                continueNext={handleContinueNext}
                onPlanOptionsClick={handleGoToPlanOptions}
              />
            );
          case SignupCardStep.SignupFormTwo:
            return <SignupFormStepTwoModified goBack={handleGoBack} />;
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.EmailSent:
            return <SignupCompleteCard onTryAgain={handleTryAgain} />;
          case SignupCardStep.Completed:
            return <SignupCompleteCard onTryAgain={handleTryAgain} />;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state, handleContinueNext, handleGoToPlanOptions, handleGoBack, handleTryAgain]);

  return (
    <div
      style={{
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 0.25s ease-in-out',
      }}
    >
      {render()}
    </div>
  );
};

export const SignupCardV2Modified = (props: BaseCardProps) => {
  return (
    <SignupFormDataProvider>
      <SignupFormControllerProvider {...props}>
        <SignupCardV2ModifiedImpl />
      </SignupFormControllerProvider>
    </SignupFormDataProvider>
  );
};
